import {useQuery} from "@tanstack/react-query";
import {API} from "api";

export type BgImageSize = {width: number; height: number; thumbnailUrl?: string};

/**
 * Natural pixel size of each background image URL. Background URLs are
 * content-addressed, so a size never changes and the cache never goes stale.
 * Only http(s) URLs are asked for: a blob: or data: URL of an upload in
 * progress has no size on the server.
 */
export function useBgImageSizes(urls: string[], enabled: boolean, thumbnailUrls: string[] = []) {
  const remote = [...new Set(urls.filter((u) => /^https?:\/\//.test(u)))].sort();
  const icons = [...new Set(thumbnailUrls.filter((u) => remote.includes(u)))].sort();
  return useQuery({
    queryKey: ["bg-image-sizes", remote, icons],
    queryFn: async (): Promise<Record<string, BgImageSize | null>> => {
      const {sizes, thumbnails} = await API.fetchBgImageSizes(remote, icons);
      if (!sizes || typeof sizes !== "object") {
        throw new Error("Invalid image-sizes response — sizes missing");
      }
      for (const url of icons) {
        if (!sizes[url] || !thumbnails?.[url]) {
          throw new Error(`Invalid image-sizes response — thumbnail missing for ${url}`);
        }
        sizes[url] = {...sizes[url], thumbnailUrl: thumbnails[url]};
      }
      return sizes;
    },
    enabled: enabled && remote.length > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Keep the sizes already known while a longer URL list loads.
    placeholderData: (previous) => previous,
  });
}
